import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './TreasuryStrategy.css';

const CDFITreasury = () => {
  return (
    <>
      <Navbar />
      <main className="treasury-page" style={{ paddingTop: '80px' }}>
        {/* Two-column layout */}
        <section className="treasury-main-section">
          <div className="treasury-container-wide">
            <div className="treasury-page-header">
              <Link to="/" className="treasury-back">&larr; Home</Link>
              <h1>CDFI Treasury Strategy</h1>
              <p>
                Helping CFOs optimize balance sheet structure, diversify capital sources, and strengthen financial positioning
              </p>
            </div>

            {/* LEFT: Balance Sheet Framework */}
            <div className="treasury-col-flow">
              <h2>Balance Sheet &amp; Capital Framework</h2>

              <div className="flow">
                {/* Step 1 */}
                <div className="flow-step">
                  <div className="flow-icon">1</div>
                  <div className="flow-card step-thesis">
                    <h3>Balance Sheet Assessment</h3>
                    <p>
                      Comprehensive review of the CDFI's current <strong>asset-liability structure</strong>,
                      capital adequacy, liquidity position, and concentration risk to establish a
                      baseline and identify optimization opportunities.
                    </p>
                    <div className="flow-tags">
                      <span>Asset-Liability Analysis</span>
                      <span>Liquidity Review</span>
                      <span>Concentration Risk</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 2 */}
                <div className="flow-step">
                  <div className="flow-icon">2</div>
                  <div className="flow-card step-firstloss">
                    <h3>Capital Source Mapping</h3>
                    <p>
                      Mapping the full landscape of available capital — <strong>CRA-motivated banks</strong>,
                      bond market issuances, platforms like <strong>InspereX</strong>, impact notes,
                      philanthropic capital, and federal programs — to identify the right mix for the
                      CDFI's growth stage and mission.
                    </p>
                    <div className="flow-tags">
                      <span>CRA Banks</span>
                      <span>Bond Market</span>
                      <span>InspereX</span>
                      <span>Impact Notes</span>
                      <span>Philanthropy</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 3 */}
                <div className="flow-step">
                  <div className="flow-icon">3</div>
                  <div className="flow-card step-cdfi">
                    <h3>Capital Strategy Design</h3>
                    <p>
                      Developing a <strong>multi-year capital plan</strong> that sequences fundraising
                      across sources, balances cost of capital against mission flexibility, and
                      ensures the CDFI maintains healthy leverage and coverage ratios.
                    </p>
                    <div className="flow-tags">
                      <span>Capital Planning</span>
                      <span>Cost of Capital</span>
                      <span>Leverage Ratios</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 4 */}
                <div className="flow-step">
                  <div className="flow-icon">4</div>
                  <div className="flow-card step-fundraise">
                    <h3>Investor Engagement &amp; Pricing</h3>
                    <p>
                      Preparing investor-ready materials, structuring term sheets, and supporting
                      conversations with <strong>regional and national banks</strong>, institutional
                      investors, and foundations — with pricing that reflects the CDFI's risk
                      profile and impact track record.
                    </p>
                    <div className="flow-tags">
                      <span>Investor Materials</span>
                      <span>Term Sheets</span>
                      <span>Pricing Strategy</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 5 */}
                <div className="flow-step">
                  <div className="flow-icon">5</div>
                  <div className="flow-card step-close">
                    <h3>Ongoing Treasury Management</h3>
                    <p>
                      Supporting the CFO with <strong>ongoing analysis</strong> — tracking maturities,
                      managing refinancing timelines, monitoring covenant compliance, and adjusting
                      strategy as the balance sheet evolves.
                    </p>
                    <div className="flow-tags">
                      <span>Maturity Tracking</span>
                      <span>Covenant Monitoring</span>
                      <span>Refinancing</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Outcomes */}
                <div className="flow-step">
                  <div className="flow-icon outcome-icon">&#10003;</div>
                  <div className="flow-card step-outcomes">
                    <h3>Outcomes</h3>
                    <div className="outcome-grid">
                      <div className="outcome-item">
                        <h4>Diversified Capital</h4>
                        <p>Reduced reliance on any single source, with a healthy mix across banks, markets, and philanthropy.</p>
                      </div>
                      <div className="outcome-item">
                        <h4>Lower Cost of Funds</h4>
                        <p>Strategic sequencing and pricing that reduces blended borrowing costs over time.</p>
                      </div>
                      <div className="outcome-item">
                        <h4>Stronger Position</h4>
                        <p>A balance sheet that supports growth, absorbs risk, and maintains CDFI certification ratios.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Capital Sources + Clarity's Role */}
            <div className="treasury-col-side">

              {/* Capital Sources Breakdown */}
              <div className="side-card">
                <h3>Typical Capital Sources</h3>
                <div className="cap-stack">
                  <div className="cap-layer cap-senior">
                    <div className="cap-label">CRA Bank Lending</div>
                    <div className="cap-pct-sm">Secured &amp; unsecured lines, term loans</div>
                  </div>
                  <div className="cap-layer cap-bond">
                    <div className="cap-label">Bond Market / InspereX</div>
                    <div className="cap-pct-sm">Rated &amp; unrated issuances, impact notes</div>
                  </div>
                  <div className="cap-layer cap-sub">
                    <div className="cap-label">PRI &amp; Impact Investors</div>
                    <div className="cap-pct-sm">Program-related investments, sub debt</div>
                  </div>
                  <div className="cap-layer cap-first">
                    <div className="cap-label">Philanthropic Capital</div>
                    <div className="cap-pct-sm">Grants, EQ2, recoverable grants</div>
                  </div>
                </div>
                <p className="cap-note">
                  Mix varies by CDFI size, certification type, and strategic priorities.
                </p>
              </div>

              {/* Where Clarity Fits In */}
              <div className="side-card clarity-role-card">
                <h3>Where Clarity Fits In</h3>
                <p className="clarity-role-intro">
                  We work alongside CDFI CFOs and leadership teams as a strategic treasury advisor:
                </p>
                <div className="clarity-role-list">
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Balance Sheet Analysis</strong>
                      <p>Deep-dive into asset-liability structure, net asset ratios, deployment rates, and capital adequacy.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Capital Source Strategy</strong>
                      <p>Evaluating and prioritizing across CRA banks, bond markets, InspereX, impact notes, and philanthropic channels.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Cost of Capital Optimization</strong>
                      <p>Modeling blended cost scenarios, maturity laddering, and refinancing strategies to lower overall funding costs.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Capital Raise Support</strong>
                      <p>Preparing pitch materials, financial projections, and investor-facing documentation for bank, bond, and PRI capital raises.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Ongoing CFO Advisory</strong>
                      <p>Regular check-ins on liquidity, covenant compliance, capital allocation, and strategic balance sheet positioning.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="treasury-cta">
          <div className="treasury-container">
            <h2>Let's Strengthen Your Balance Sheet</h2>
            <p>
              Whether you're diversifying capital sources, preparing for a bond issuance, or
              rethinking your overall treasury strategy, we're here to help.
            </p>
            <Link to="/" state={{ scrollTo: 'contact' }} className="treasury-cta-btn">
              Get in Touch &rarr;
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CDFITreasury;
